import React, { useEffect, useState } from "react";
import { pixel } from "@/lib/metaPixel";
import { Link, useParams } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { CheckCircleIcon, XCircleIcon } from "@phosphor-icons/react";
import { storeApi } from "@/lib/storeApi";
import { gbp, pct } from "@/lib/customerApi";
import StoreTopbar from "../StoreTopbar";
import HubFooter from "../HubFooter";
import BotKpis from "./BotKpis";
import MonthlyTable from "./MonthlyTable";
import { RiskNotice } from "../RiskNotice";

const Panel = ({ title, children, testid, accent = "#38BDF8" }) => (
  <div data-testid={testid} className="border border-blue-900/40 bg-[#020617]/70 rounded-xl p-5">
    <div className="mono text-[10px] uppercase tracking-widest" style={{ color: accent }}>{title}</div>
    <div className="mt-4">{children}</div>
  </div>
);

const CHECKS = { years: "Every year profitable on its own", spread_x2: "Still profitable with double spreads",
  settings: "Survives settings nudged ±20%", monte_carlo: "Monte Carlo 95% worst-case DD under 35%" };

function Robustness({ r }) {
  if (!r) return <div className="text-xs text-[#64748B]">Not yet run for this bot.</div>;
  return (
    <ul className="space-y-2 text-sm">
      {Object.entries(CHECKS).map(([k, label]) => {
        const v = r.checks?.[k];
        const Icon = v ? CheckCircleIcon : XCircleIcon;
        return (
          <li key={k} data-testid={`robust-${k}`} className="flex items-center gap-2">
            <Icon size={16} weight="fill" className={v ? "text-[#2ECA7F]" : v === false ? "text-[#F87171]" : "text-[#475569]"} />
            <span className={v == null ? "text-[#64748B]" : ""}>{label}{v == null ? " (not run)" : ""}</span>
          </li>
        );
      })}
      <li className="text-xs text-[#94A3B8] pt-2">Monte Carlo worst-case drawdown (95%): {pct(r.monte_carlo_dd95)} · settings stress passed {r.settings_stress_ok ?? 0}/{r.settings_stress_total ?? 0}</li>
      {r.double_spread && <li className="text-xs text-[#94A3B8]">With double spreads: {gbp(r.double_spread.net_profit)}, PF {r.double_spread.profit_factor}, DD {pct(r.double_spread.max_dd_pct)}</li>}
    </ul>
  );
}

function Holdout({ h, b }) {
  if (!h) return <div className="text-xs text-[#64748B]">2025+ unseen-data exam pending. Bots with a positive result are also shown in the "Passed the 2025+ unseen-data backtest" section.</div>;
  const months = Math.max(Number(h.backtest_months) || 1, 1);
  return (
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div><div className="text-[#64748B] mono text-[9px] uppercase">2022-24 backtest (simulated)</div><div className="text-lg text-[#2ECA7F]">{gbp(b.net_profit)}</div><div className="text-xs text-[#94A3B8]">{gbp(b.avg_monthly)}/mo · PF {b.profit_factor} · DD {pct(b.max_dd_pct)}</div></div>
      <div><div className="text-[#64748B] mono text-[9px] uppercase">2025+ unseen backtest ({h.backtest_from} → {h.backtest_to})</div><div className={`text-lg ${Number(h.net_profit) > 0 ? "text-[#2ECA7F]" : "text-[#F87171]"}`}>{gbp(h.net_profit)}</div><div className="text-xs text-[#94A3B8]">{gbp(Number(h.net_profit) / months)}/mo · PF {h.profit_factor ?? "-"} · DD {pct(h.max_dd_pct)} · {h.total_trades} trades</div></div>
      <p className="col-span-2 text-xs text-[#94A3B8]">Both start from {gbp(b.start_balance)}. Both are backtests (simulations). The unseen period shows how the fixed rules behaved on data they weren't built on; neither is a reliable indicator of future performance.</p>
    </div>
  );
}

export default function BotPage() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    storeApi.get(`/store/mt5/bot/${id}`).then((r) => { setB(r.data); pixel.viewContent(r.data); }).catch(() => setErr(true));
  }, [id]);
  const years = b?.years ? Object.entries(b.years).map(([y, v]) => ({ y, v: Math.round(v) })) : [];
  return (
    <div data-testid="mt5-bot-page" className="relative min-h-screen bg-[#050914] text-[#F1F5F9]">
      <StoreTopbar subtitle="Bot details" />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        {err && <div data-testid="bot-not-found" className="text-[#F87171]">Bot not found. <Link to="/mt5" className="underline">Back to bots</Link></div>}
        {!b && !err && <div className="mono text-xs text-[#64748B]">Loading…</div>}
        {b && (
          <>
            <Link to="/mt5#bots" className="inline-block py-2 mono text-[11px] uppercase tracking-widest text-[#94A3B8] hover:text-white" data-testid="bot-back">← All bots</Link>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
              <div>
                <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#FBBF24]">{b.symbol_name} · {b.timeframe} chart</div>
                <h1 data-testid="bot-name" className="text-3xl sm:text-4xl font-medium tracking-tight mt-2">{b.name}</h1>
                <p className="text-sm text-[#94A3B8] mt-3 max-w-2xl leading-relaxed" data-testid="bot-description">
                  <b className="text-[#CBD5E1]">{b.strategy}.</b> {b.description} Designed for a {b.symbol} {b.period} chart. Its trading rules are fixed in the released version and aren't changed remotely.
                </p>
              </div>
              <Link to={`/account?pick=${b.store_id}`} data-testid="bot-choose-cta" className="mono text-[11px] uppercase tracking-widest px-6 py-3 rounded-lg bg-[#FBBF24] text-[#050914] hover:scale-[1.03] transition-transform">
                Choose this bot
              </Link>
            </div>
            {!b.listed && <div className="mt-4 text-xs text-[#F87171]" data-testid="bot-unlisted">This bot has dropped out of the current top 5 and can't be newly picked.</div>}
            <BotKpis b={b} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
              <div className="lg:col-span-2">
                <Panel title={`Backtest equity curve (simulated) · ${gbp(b.start_balance)} start, profits compound`} testid="bot-equity">
                  <div className="h-64">
                    <ResponsiveContainer>
                      <AreaChart data={b.monthly}>
                        <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                        <XAxis dataKey="month" stroke="#64748B" fontSize={10} />
                        <YAxis stroke="#64748B" fontSize={10} tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
                        <Tooltip contentStyle={{ background: "#020617", border: "1px solid #1E3A8A" }} formatter={(v) => gbp(v)} />
                        <Area dataKey="end_equity" stroke="#38BDF8" fill="#38BDF8" fillOpacity={0.15} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
              </div>
              <Panel title="Backtest profit by year (simulated)" testid="bot-years" accent="#FBBF24">
                {years.length ? (
                  <div className="h-64">
                    <ResponsiveContainer>
                      <BarChart data={years}>
                        <XAxis dataKey="y" stroke="#64748B" fontSize={10} />
                        <YAxis stroke="#64748B" fontSize={10} tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
                        <Tooltip contentStyle={{ background: "#020617", border: "1px solid #1E3A8A" }} formatter={(v) => gbp(v)} />
                        <Bar dataKey="v">{years.map((y) => <Cell key={y.y} fill={y.v >= 0 ? "#2ECA7F" : "#F87171"} />)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : <div className="text-xs text-[#64748B]">Yearly split not available yet.</div>}
              </Panel>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
              <Panel title="Backtest vs. unseen 2025+ data" testid="bot-holdout" accent="#2ECA7F"><Holdout h={b.holdout_detail} b={b} /></Panel>
              <Panel title="Robustness checks" testid="bot-robustness" accent="#A78BFA"><Robustness r={b.robustness_checks} /></Panel>
              <Panel title="Default settings" testid="bot-settings" accent="#22D3EE">
                <ul className="text-sm space-y-1.5">
                  {Object.entries(b.settings || {}).map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-3"><span className="text-[#94A3B8]">{k}</span><span className="mono">{v}</span></li>
                  ))}
                  <li className="flex justify-between gap-3"><span className="text-[#94A3B8]">Test model</span><span className="mono">{b.test_model === "real_ticks" ? "Real ticks" : b.test_model}</span></li>
                  <li className="flex justify-between gap-3"><span className="text-[#94A3B8]">Starting balance</span><span className="mono">{gbp(b.start_balance)} (1:30)</span></li>
                </ul>
              </Panel>
            </div>
            <MonthlyTable rows={b.monthly} />
            <RiskNotice testid="bot-risk" className="mt-8" />
            <p className="mt-8 text-xs text-[#64748B] leading-relaxed max-w-4xl" data-testid="bot-disclaimer">
              All results on this page are hypothetical backtests from the MetaTrader 5 strategy tester ({b.test_model === "real_ticks" ? "real ticks" : b.test_model},{" "}
              {gbp(b.start_balance)} start at 1:30 leverage, {b.backtest_from} → {b.backtest_to}). They weren't achieved with real or demo money. Limitations: they're
              produced with hindsight, this bot was selected from many tested bots because it did well on this history, they don't reflect every real cost
              (slippage, requotes, swap changes, outages), and profits compound, so later months look much larger. Your results will differ with your broker,
              balance and settings. Showing this bot is not a recommendation to trade it or the instrument it trades.
            </p>
          </>
        )}
      </main>
      <HubFooter />
    </div>
  );
}
